import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Tab,
  Tabs,
  LinearProgress,
  Avatar,
} from '@mui/material';
import {
  School,
  Grade,
  EventNote,
  TrendingUp,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { dashboardAPI } from '../../services/api';
import StudentAttendance from '../Student/StudentAttendance';
import StudentMarks from '../Student/StudentMarks';
import StudentExams from '../Student/StudentExams';
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const StudentDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState({
    attendance: { present: 0, absent: 0, percentage: 0 },
    recentExams: [],
    upcomingExams: [],
    profile: {},
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const response = await dashboardAPI.getStudentStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    }
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color, progress }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <Box
            sx={{
              bgcolor: `${color}.100`,
              borderRadius: '50%',
              p: 1,
              mr: 2,
            }}
          >
            <Icon sx={{ color: `${color}.main`, fontSize: 28 }} />
          </Box>
          <Box flex={1}>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        {progress !== undefined && (
          <Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 5,
                bgcolor: `${color}.50`,
                '& .MuiLinearProgress-bar': {
                  bgcolor: `${color}.main`,
                },
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {progress}% Complete
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const attendanceData = {
    labels: ['Present', 'Absent'],
    datasets: [
      {
        data: [stats.attendance.present, stats.attendance.absent],
        backgroundColor: ['#4caf50', '#f44336'],
        borderWidth: 0,
      },
    ],
  };

  const marksData = {
    labels: stats.recentExams.map((exam) => exam.subject),
    datasets: [
      {
        label: 'Marks Obtained',
        data: stats.recentExams.map((exam) => exam.marks),
        backgroundColor: '#2196f3',
      },
      {
        label: 'Total Marks',
        data: stats.recentExams.map((exam) => exam.total),
        backgroundColor: '#e3f2fd',
      },
    ],
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header with Profile */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Box display="flex" alignItems="center" gap={3}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: 'white',
              color: 'primary.main',
              fontSize: 32,
            }}
          >
            {user?.full_name?.charAt(0)}
          </Avatar>
          <Box flex={1} color="white">
            <Typography variant="h4" fontWeight="bold">
              {user?.full_name}
            </Typography>
            <Typography variant="body1">
              Class {user?.standard} • Enrollment: {stats.profile.enrollment_no}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={EventNote}
            title="Attendance"
            value={`${stats.attendance.percentage}%`}
            subtitle={`${stats.attendance.present} days present`}
            color="success"
            progress={stats.attendance.percentage}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={Grade}
            title="Average Score"
            value={stats.profile.average_score || 'N/A'}
            subtitle="Last exam"
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={School}
            title="Class Rank"
            value={stats.profile.rank || 'N/A'}
            subtitle={`Out of ${stats.profile.total_students || 0}`}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={TrendingUp}
            title="Performance"
            value={stats.profile.grade || 'N/A'}
            subtitle="Overall grade"
            color="primary"
          />
        </Grid>
      </Grid>

      {/* Main Content */}
      <Paper sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Overview" />
            <Tab label="Attendance" />
            <Tab label="Marks" />
            <Tab label="Exams" />
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <Grid container spacing={3}>
              {/* Attendance Chart */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Attendance Distribution
                </Typography>
                <Box sx={{ maxWidth: 300, mx: 'auto' }}>
                  <Pie data={attendanceData} />
                </Box>
              </Grid>

              {/* Recent Marks */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Recent Exam Performance
                </Typography>
                {stats.recentExams.length > 0 ? (
                  <Bar data={marksData} options={{ responsive: true }} />
                ) : (
                  <Typography color="text.secondary">No recent exams</Typography>
                )}
              </Grid>

              {/* Upcoming Exams */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Upcoming Examinations
                </Typography>
                {stats.upcomingExams.length > 0 ? (
                  <Grid container spacing={2}>
                    {stats.upcomingExams.map((exam, index) => (
                      <Grid item xs={12} md={4} key={index}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="h6">{exam.subject}</Typography>
                            <Typography color="text.secondary">
                              {exam.exam_name}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              Date: {exam.date}
                            </Typography>
                            <Typography variant="body2">
                              Time: {exam.time}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography color="text.secondary">No upcoming exams</Typography>
                )}
              </Grid>
            </Grid>
          )}

          {activeTab === 1 && (
            <StudentAttendance />
          )}

          {activeTab === 2 && (
            <StudentMarks />
          )}

          {activeTab === 3 && (
            <StudentExams />
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default StudentDashboard;
